"use no memo";
import BarMusicPlayer from "@components/BarMusicPlayer";
import LBox from "@components/LBox";
import LIcon, { IconName } from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { useCDNKey } from "@hooks/trpc/useCDNKey";
import useSessionTracking from "@hooks/useSessionTracking";
import type {
  BottomTabBarButtonProps,
  BottomTabBarProps,
} from "expo-router/build/react-navigation/bottom-tabs";
import { Redirect, Tabs, usePathname } from "expo-router";
import {
  BackHandler,
  GestureResponderEvent,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import { TABS, LTabs } from "@utils/index";
import TopBar from "@components/TopBar";
import { BlurView } from "expo-blur";
import { CommonActions } from "expo-router/react-navigation";
import { useFollowings } from "@hooks/trpc/useFollow";
import React, { useCallback, useEffect } from "react";
import useInternetStatus from "@utils/internetStatus";
import {
  useAnimatedStyle,
  withTiming,
  Easing,
  useSharedValue,
} from "react-native-reanimated";
import { theme } from "@theme";
import { useMe } from "@hooks/trpc/useMe";
import UserAvatar from "@components/UserAvatar";
import useInvalidateLibrary from "@hooks/useInvalidateLibrary";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import { useTabBarHeight } from "@hooks/useTabBarHeight";
import { usePlayer } from "@utils/player/core/usePlayer";
import { selectIsQueueEmpty } from "@utils/player/core/selectors";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { shouldLazyLoadTab } from "@utils/navigation/helpers";

export const unstable_settings = {
  // "home" instead of "index" so that backBehavior="history" falls back to
  // the home tab directly. Using "index" caused a Redirect round-trip that
  // blanked all tab content on Android back-button press.
  initialRouteName: "home",
};

const TAB_BUTTON_HEIGHT = 60;

const TAB_AVATAR_BORDER_COLOR = "#FFFFFF";

const TabBarIcon = ({
  focused,
  iconColor,
  name,
  showUserAvatar,
}: {
  focused: boolean;
  iconColor?: string;
  name?: IconName;
  showUserAvatar?: boolean;
}) => {
  const {
    query: { data: user },
  } = useMe();
  return (
    <LBox justifyContent="center" flexDirection="row" mb="s1">
      {name === "library" ? (
        <LIcon colors={{ color: iconColor }} name={name} size={24} />
      ) : name ? (
        <LIcon name={name} size={24} />
      ) : null}
      {showUserAvatar ? (
        <View
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            borderWidth: focused ? 1.5 : 0,
            borderColor: TAB_AVATAR_BORDER_COLOR,
            overflow: "hidden",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <UserAvatar
            key={user?.id}
            image={user?.image}
            defaultAvatarColors={user?.defaultAvatarColors}
            size={focused ? 22 : 24}
            imageSize="small"
            borderRadius="zero"
          />
        </View>
      ) : null}
    </LBox>
  );
};

const TabBarLabel = ({ label }: { label: string }) => (
  <LBox>
    <LText
      fontWeight={400}
      color="white"
      lineHeight={12}
      textAlign={"center"}
      variant="bodyS2"
    >
      {label}
    </LText>
  </LBox>
);

let hapticsModule: typeof import("expo-haptics") | null = null;

const triggerTabHaptic = async () => {
  if (Platform.OS === "web") return;
  try {
    if (!hapticsModule) {
      hapticsModule = await import("expo-haptics");
    }
    await hapticsModule.impactAsync(hapticsModule.ImpactFeedbackStyle.Light);
  } catch {
    // Haptics are optional
  }
};

const TabBarButton = (
  { children, onPress }: BottomTabBarButtonProps,
  tab: LTabs,
) => {
  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handlePressIn = useCallback(() => {
    scale.value = withTiming(0.95, {
      duration: 100,
      easing: Easing.out(Easing.quad),
    });
  }, [scale]);

  const handlePressOut = useCallback(() => {
    scale.value = withTiming(1, {
      duration: 150,
      easing: Easing.out(Easing.quad),
    });
  }, [scale]);

  return (
    <LBox width={`${100 / TABS.length}%`} key={tab}>
      <LPressable
        key={tab}
        onPress={(event: GestureResponderEvent) => {
          triggerTabHaptic().catch(() => {});
          onPress?.(event);
        }}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        sentryEventName={`tab_press_${tab}`}
      >
        <LBox height={TAB_BUTTON_HEIGHT} style={animatedStyle}>
          {children}
        </LBox>
      </LPressable>
    </LBox>
  );
};

const TabBarBlur = () => {
  if (Platform.OS !== "ios") {
    return (
      <>
        <LBox
          position="absolute"
          top={-1}
          left={0}
          right={0}
          bottom={0}
          opacity={0.2}
          bg="white"
          borderTopLeftRadius="l"
          borderTopRightRadius="l"
        />
        <LBox
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="miniPlayerBackground"
          borderTopLeftRadius="l"
          borderTopRightRadius="l"
        />
      </>
    );
  }

  return (
    <LBox
      overflow="hidden"
      borderTopLeftRadius="l"
      borderTopRightRadius="l"
      position="absolute"
      top={-1}
      left={0}
      right={0}
      bottom={0}
    >
      <BlurView
        intensity={60}
        experimentalBlurMethod={"none"}
        tint="systemMaterialDark"
        style={{
          ...StyleSheet.absoluteFill,
        }}
      >
        <LBox
          position="absolute"
          top={-1}
          left={0}
          right={0}
          bottom={0}
          bg="white"
          opacity={0.05}
          borderTopLeftRadius="l"
          borderTopRightRadius="l"
        />
      </BlurView>
    </LBox>
  );
};

const TabBarButtons = React.memo(
  ({
    descriptors,
    navigation,
    state,
  }: {
    descriptors: BottomTabBarProps["descriptors"];
    navigation: BottomTabBarProps["navigation"];
    state: BottomTabBarProps["state"];
  }) => {
    const isOnline = useInternetStatus();
    const insets = useSafeAreaInsets();
    const androidBottomInset = Platform.OS === "android" ? insets.bottom : 0;
    const tabBarButtonHeightStyle = useAnimatedStyle(() => {
      return {
        height: withTiming(
          isOnline ? TAB_BUTTON_HEIGHT : TAB_BUTTON_HEIGHT - 12,
          {
            duration: 500,
          },
        ),
      };
    });
    const tabBarHeight = useTabBarHeight();
    return (
      <LBox
        key="tabbar-buttons-container"
        justifyContent="center"
        flexDirection="row"
        height={tabBarHeight}
        pt="s2"
        px="s2"
        style={{ paddingBottom: androidBottomInset }}
      >
        {state.routes.map((route, index) => {
          const { tabBarButton, tabBarIcon, tabBarLabel } =
            descriptors[route.key].options;
          const focused = index === state.index;

          const onPress = () => {
            const event = navigation.emit({
              type: "tabPress",
              target: route.key,
              canPreventDefault: true,
            });

            if (!focused && !event.defaultPrevented) {
              navigation.dispatch({
                ...CommonActions.navigate(route),
                target: state.key,
              });
            }
          };

          return tabBarButton?.({
            children: (
              <LBox
                style={tabBarButtonHeightStyle}
                overflow="hidden"
                justifyContent="center"
                width="100%"
                opacity={focused ? 1 : 0.6}
              >
                {tabBarIcon?.({ color: "", focused, size: 0 })}
                {typeof tabBarLabel === "function" ? (
                  tabBarLabel?.({
                    children: "",
                    color: "",
                    focused,
                    position: "below-icon",
                  })
                ) : (
                  <LText>{tabBarLabel}</LText>
                )}
              </LBox>
            ),
            onPress,
          });
        })}
      </LBox>
    );
  },
);
TabBarButtons.displayName = "TabBarButtons";

const TabBar = ({ children }: { children: React.ReactNode }) => {
  const isSongQueueEmpty = usePlayer(selectIsQueueEmpty);
  const hideTabButtons = useNavigationStore((state) => state.isStorePath);
  const tabBarHeight = useTabBarHeight();
  const progressStyle = useAnimatedStyle(() => ({
    paddingHorizontal: withTiming(hideTabButtons ? theme.spacing.s2 : 0, {
      duration: 200,
      easing: Easing.inOut(Easing.ease),
    }),
  }));

  const containerStyle = useAnimatedStyle(() => {
    const translateAmount = tabBarHeight - 20;
    return {
      transform: [
        {
          translateY: withTiming(hideTabButtons ? translateAmount : 0, {
            duration: 350,
            easing: Easing.inOut(Easing.ease),
          }),
        },
      ],
    };
  });

  const tabBarButtonsContainerStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(hideTabButtons ? 0 : 1, {
        duration: 200,
        easing: Easing.inOut(Easing.ease),
      }),
    };
  });

  return (
    <LBox bottom={0} position="absolute" width="100%" style={containerStyle}>
      <TabBarBlur />
      <BarMusicPlayer progressStyle={progressStyle} />
      <LBox
        style={tabBarButtonsContainerStyle}
        borderTopWidth={1}
        borderTopColor="whiteTranslucent10"
        bg="tabBarBg"
        borderTopLeftRadius={isSongQueueEmpty ? "l" : "zero"}
        borderTopRightRadius={isSongQueueEmpty ? "l" : "zero"}
      >
        {children}
      </LBox>
    </LBox>
  );
};

type TabConfig = {
  route: string;
  config?: {
    label: string;
    icon?: IconName;
    iconColor?: string;
  };
};

const TabMapping: Record<LTabs, TabConfig> = {
  home: {
    route: "home",
    config: {
      icon: "tabs_home",
      label: "Home",
    },
  },
  explore: {
    route: "explore",
    config: {
      icon: "tabs_experiences",
      label: "Explore",
    },
  },
  library: {
    route: "library",
    config: {
      icon: "library",
      iconColor: "#C4C4C4",
      label: "Library",
    },
  },
  profile: {
    route: "profile",
    config: {
      label: "Profile",
    },
  },
};

const formatTabScreenOptions = (tab: LTabs) => {
  const tabConfig = TabMapping[tab].config;
  if (!tabConfig) return { href: null };

  return {
    tabBarButton: (props: BottomTabBarButtonProps) => TabBarButton(props, tab),
    tabBarIcon: ({ focused }: { focused: boolean }) => (
      <TabBarIcon
        iconColor={tabConfig.iconColor}
        name={tabConfig.icon}
        focused={focused}
        showUserAvatar={tab === "profile"}
      />
    ),
    tabBarLabel: () => <TabBarLabel label={tabConfig.label} />,
    lazy: shouldLazyLoadTab(tab),
  };
};

const FollowingsWrapper = () => {
  useFollowings(true);
  return null;
};

const CDNWrapper = () => {
  useCDNKey();
  return null;
};
const SessionTrackerWrapper = () => {
  useSessionTracking();
  return null;
};
const InvalidateLibraryWrapper = () => {
  useInvalidateLibrary();
  return null;
};

const TabBarWrapper = (props: BottomTabBarProps) => {
  return (
    <TabBar>
      <TabBarButtons
        descriptors={props.descriptors}
        navigation={props.navigation}
        state={props.state}
      />
    </TabBar>
  );
};

const TabLayout = () => {
  const {
    query: { data: user },
  } = useMe();
  const insets = useSafeAreaInsets();
  const tabBarHeight = useTabBarHeight();
  const androidBottomInset = Platform.OS === "android" ? insets.bottom : 0;

  // Prevent the Android back button from popping (tabs) out of the parent
  // Stack when there's nowhere left to go. On home (the initial route) the
  // tab navigator won't consume the event; without this it bubbles to the
  // AppLayout Stack which pops (tabs) and blanks the screen.
  const pathname = usePathname();
  useEffect(() => {
    if (Platform.OS !== "android") return;
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
      if (pathname === "/home" || pathname === "/") {
        BackHandler.exitApp();
        return true;
      }
      return false;
    });
    return () => sub.remove();
  }, [pathname]);

  if (user?.onboardingStep === "email_verification_pending") {
    return <Redirect href="/auth/verify-email" />;
  }

  return (
    <>
      <FollowingsWrapper />
      <CDNWrapper />
      <SessionTrackerWrapper />
      <InvalidateLibraryWrapper />
      <Tabs
        initialRouteName="home"
        backBehavior="history"
        tabBar={TabBarWrapper}
        screenOptions={{
          tabBarStyle: {
            backgroundColor: "transparent",
            height: tabBarHeight,
            paddingBottom: androidBottomInset,
            borderTopWidth: 0,
            position: "absolute",
          },
          sceneStyle: {
            backgroundColor: "transparent",
          },
          headerShown: false,
        }}
      >
        {TABS.map((tab) => (
          <Tabs.Screen
            name={TabMapping[tab].route}
            key={tab}
            options={formatTabScreenOptions(tab)}
          />
        ))}
        <Tabs.Screen name="index" options={{ href: null }} />
        <Tabs.Screen name="inbox" options={{ href: null }} />
      </Tabs>
      <TopBar />
    </>
  );
};

export default TabLayout;
